import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Download, Mail, HelpCircle, ArrowLeft, FileText, FolderArchive, AlertCircle } from 'lucide-react';
import { IonProgressBar } from '@ionic/react';
import { toast } from "@/utils/toast";
import { toastText } from "@/utils/toastTemplates";
import { nativePost } from "@/utils/nativeHttp";
import { appThemeDark } from "@/utils/appTheme";
import { getVisitorId } from "@/utils/trackPageView";
import { loadJsPDF } from "@/utils/pdfEngines";
import { renderLayout, fetchPublishedLayout } from "@/utils/layoutEngine";
import Leaf3D from '@/components/Leaf3D';
import { fileToPreviewPages } from '@/components/DocumentPreviewModal';
import { setPendingDownload, deliverDownload, openDownloadInNewTab } from '@/utils/pendingDownload';

// Import all generators
import { generateAndDownloadPaystub, getIndividualPaystubFilename, getMultiplePaystubsZipFilename } from '@/utils/paystubGenerator';
import {
  generateAndDownloadCanadianPaystub,
  getIndividualPaystubFilename as getCanadianPaystubFilename,
  getMultiplePaystubsZipFilename as getCanadianPaystubsZipFilename,
} from '@/utils/canadianPaystubGenerator';
import {
  generateAndDownloadUkPayslip,
  getIndividualPaystubFilename as getUkPayslipFilename,
  getMultiplePaystubsZipFilename as getUkPayslipsZipFilename,
} from '@/utils/ukPayslipGenerator';
import { generateAndDownloadW2 } from '@/utils/w2Generator';
import { generateAndDownloadW9 } from '@/utils/w9Generator';
import { generateAndDownload1099NEC } from '@/utils/1099necGenerator';
import { generateAndDownload1099MISC } from '@/utils/1099miscGenerator';
import { generateAndDownloadBankStatement } from '@/utils/bankStatementGenerator';
import { generateAndDownloadResume } from '@/utils/resumeGenerator';
import { generateAndDownloadOfferLetter } from '@/utils/offerLetterGenerator';
import { generateAndDownloadCeaseAndDesist } from '@/utils/ceaseAndDesistGenerator';
import { generateAndDownloadLegalDocument } from '@/utils/legalDocumentGenerator';
import { generateAndDownloadPowerOfAttorney } from '@/utils/powerOfAttorneyGenerator';
import { generateAndDownloadCommercialLease } from '@/utils/commercialLeaseGenerator';
import { generateAndDownloadScheduleC } from '@/utils/scheduleCGenerator';
import { generateAndDownloadUtilityBill } from '@/utils/utilityBillGenerator';
import { generateAndDownloadVehicleBillOfSale } from '@/utils/vehicleBillOfSaleGenerator';

// Import email utility for sending PDF attachments
import { sendDownloadEmailWithPdf } from '@/utils/emailWithPdf';

// Notifications
import { addGeneratingNotification, markNotificationReady, markNotificationError, setNotificationDocId } from '@/utils/appNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// ── Animated processing screen (whodat-style search screen, MintSlip green) ──
// A floating, spinning 3D leaf over a soft glow, the brand line, an Ionic
// progress bar, and cycling status messages. Background follows the dark/light
// mode the user picked in the app.
function ProcessingScreen({ title, messages }) {
  const dark = typeof window !== "undefined" && appThemeDark();
  const [progress, setProgress] = useState(0);

  // Ease toward 92% and hold — the real work finishes the screen by unmounting
  useEffect(() => {
    const iv = setInterval(() => {
      setProgress((p) => Math.min(0.92, p + (0.92 - p) * 0.03 + 0.003));
    }, 120);
    return () => clearInterval(iv);
  }, []);

  const msgIndex = Math.min(messages.length - 1, Math.floor((progress / 0.92) * messages.length));
  const bg = dark ? "#121212" : "#f6faf7";
  const ink = dark ? "#ffffff" : "#0f172a";
  const sub = dark ? "rgba(255,255,255,0.62)" : "#64748b";
  const track = dark ? "rgba(255,255,255,0.12)" : "rgba(52,211,153,0.16)";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <style>{`
        @keyframes msMsgIn { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div style={{ maxWidth: 380, width: "100%", textAlign: "center" }}>
        {/* Real 3D leaf (Three.js) */}
        <div style={{ marginBottom: 8 }}>
          <Leaf3D height={190} />
        </div>

        <IonProgressBar value={progress} style={{ "--progress-background": "#34d399", "--background": track, borderRadius: 99, height: 6, overflow: "hidden", marginBottom: 22 }} />

        <h2 style={{ fontFamily: "Outfit, sans-serif", fontWeight: 700, fontSize: 20, color: ink, margin: "0 0 8px" }}>{title}</h2>
        <p key={msgIndex} style={{ color: sub, fontSize: 15, margin: 0, minHeight: 22, animation: "msMsgIn 0.35s ease" }}>
          {messages[msgIndex]}
        </p>
      </div>
    </div>
  );
}

const VERIFY_MESSAGES = [
  "Contacting secure payment server…",
  "Verifying your payment…",
  "Confirming your order details…",
  "Payment locked in, almost there…",
];

const GENERATE_MESSAGES = [
  "Warming up the document engine…",
  "Laying out your document…",
  "Crunching totals and taxes…",
  "Rendering crisp PDF pages…",
  "Applying finishing touches…",
  "Packaging your download…",
];

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderType = searchParams.get('type') || 'paystub';
  const sessionId = searchParams.get('session_id');
  // Set by the in-app embedded card checkout (PaymentModal) — the payment was
  // already confirmed on-session by Stripe.js, so there is no checkout session
  // to verify; we go straight to generating.
  const paymentIntentId = searchParams.get('payment_intent');
  const fileCount = parseInt(searchParams.get('count') || '1', 10);
  const isFromApp = searchParams.get('source') === 'app';
  // A subscribed download: no payment to verify — the plan covers it. The
  // page still animates, generates, delivers and archives the same way.
  const isSubDownload = searchParams.get('sub') === '1';
  // A document the admin priced at zero: no payment and no plan download;
  // the server records a $0 order and the page generates as usual.
  const isFreeDownload = searchParams.get('free') === '1';

  // Where "back" leads for purchases made inside the app
  const APP_RETURN_PATHS = {
    'paystub': '/app/paystubs',
    'canadian-paystub': '/app/canadian-paystub',
    'uk-payslip': '/app/uk-payslips',
    'ai-resume': '/app/resumes',
    'offer-letter': '/app/resumes',
    'w2': '/app/tax-forms', 'w9': '/app/tax-forms', '1099-nec': '/app/tax-forms',
    '1099-misc': '/app/tax-forms', 'schedule-c': '/app/tax-forms',
    'cease-and-desist': '/app/legal-forms', 'power-of-attorney': '/app/legal-forms',
    'vehicle-bill-of-sale': '/app/legal-forms', 'legal-document': '/app/legal-forms',
    'commercial-lease': '/app/business-forms', 'utility-bill': '/app/business-forms',
    'bank-statement': '/app/business-forms',
  };
  // An in-app flow can name where to come back to; only /app paths are
  // honored
  const returnParam = searchParams.get('return') || '';
  const appReturnTo = (/^\/app\/[\w\-/]*$/.test(returnParam) ? returnParam : null) || APP_RETURN_PATHS[orderType] || '/app';
  
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [error, setError] = useState(null);
  const [documentGenerated, setDocumentGenerated] = useState(false);

  // Post-purchase upsell — a discounted "matching document" with a countdown,
  // configured in admin Site Settings. The backend opens this IP's window on
  // the first view for this payment (reloads reuse it) and enforces the
  // discount at charge time.
  const [upsell, setUpsell] = useState(null);
  const [upsellLeft, setUpsellLeft] = useState(0);
  // In-app purchases return to the app as soon as the download fires, so the
  // offer is requested right away and handed to the app (which shows it as a
  // paywall-style screen) instead of being rendered here.
  const upsellFetchRef = useRef(null);
  // The $0 purchase a plan download records: the archive is stamped with its
  // id (there is no Stripe reference) so the admin purchase modal finds the file.
  const planPurchaseIdRef = useRef(null);
  useEffect(() => {
    if (!paymentIntentId || !isFromApp) return;
    upsellFetchRef.current = fetch(`${BACKEND_URL}/api/upsell/offer?documentType=${encodeURIComponent(orderType)}&pi=${encodeURIComponent(paymentIntentId)}`)
      .then(r => r.json()).catch(() => null);
  }, []); // eslint-disable-line
  // A small first-page image of the document just bought: the upsell screen
  // blurs it behind the offer, the way the paywall blurs the checkout preview.
  const upsellPreviewFor = async (blob) => {
    try {
      if (!blob || blob.type === 'application/zip') return null;
      const pages = await fileToPreviewPages(blob);
      const src = pages && pages[0];
      if (!src) return null;
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
      const w = 480, h = Math.max(1, Math.round(img.height * (w / img.width)));
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      return c.toDataURL('image/jpeg', 0.72);
    } catch { return null; }
  };
  const stashUpsellForApp = async (blob) => {
    if (!upsellFetchRef.current) return;
    const within = (p, ms) => Promise.race([p, new Promise(r => setTimeout(() => r(null), ms))]);
    const [d, previewImage] = await Promise.all([within(upsellFetchRef.current, 2500), within(upsellPreviewFor(blob), 2500)]);
    if (d && d.enabled && d.active && Array.isArray(d.targets) && d.targets.length && d.discountPercent > 0) {
      try { sessionStorage.setItem('ms:upsellOffer', JSON.stringify({ ...d, boughtType: orderType, boughtLabel: getDocumentTypeDisplay(orderType), previewImage: previewImage || null })); } catch {}
    }
  };
  useEffect(() => {
    if (!documentGenerated || !isFromApp || isSubDownload || isFreeDownload) return;
    let on = true;
    fetch(`${BACKEND_URL}/api/upsell/offer?documentType=${encodeURIComponent(orderType)}&pi=${encodeURIComponent(paymentIntentId || sessionId || "")}`)
      .then(r => r.json())
      .then(d => { if (on && d && d.enabled && d.active && Array.isArray(d.targets) && d.targets.length) setUpsell(d); })
      .catch(() => {});
    return () => { on = false; };
  }, [documentGenerated]); // eslint-disable-line
  useEffect(() => {
    if (!upsell?.expiresAt) return;
    const tick = () => setUpsellLeft(Math.max(0, Math.floor((new Date(upsell.expiresAt) - Date.now()) / 1000)));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [upsell]);
  const takeUpsell = (target) => {
    try {
      sessionStorage.setItem("ms:upsell", JSON.stringify({
        expiresAt: upsell.expiresAt, percent: upsell.discountPercent,
        targets: upsell.targets.map(t => t.documentType),
      }));
    } catch {}
    navigate(target.path);
  };
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  // Ref to prevent double download (React StrictMode runs effects twice)
  const hasStartedRef = React.useRef(false);
  
  // Determine if it's a ZIP file based on count OR filename extension
  const isZipFile = fileCount > 1 || fileName.endsWith('.zip');

  // Clear any stale download data at the start of a new payment verification
  useEffect(() => {
    // Clear old download URLs when a new session starts
    if (sessionId || paymentIntentId) {
      localStorage.removeItem('lastDownloadUrl');
      localStorage.removeItem('lastDownloadFileName');
      sessionStorage.removeItem('lastDownloadUrl');
      sessionStorage.removeItem('lastDownloadFileName');
    }
  }, [sessionId, paymentIntentId]);

  // Verify payment and generate document
  const verifyAndGenerate = useCallback(async () => {
    if (isFreeDownload) {
      try {
        const token = localStorage.getItem('userToken');
        const template = localStorage.getItem('pendingSubTemplate');
        const res = await nativePost(`${BACKEND_URL}/api/free-download`,
          { documentType: orderType, ...(template ? { template } : {}), count: fileCount, visitorId: getVisitorId(), email: localStorage.getItem('pendingCustomerEmail') || '' },
          token ? { Authorization: `Bearer ${token}` } : {});
        const data = (typeof res.json === 'function' ? await res.json().catch(() => ({})) : res.data) || {};
        if (!res.ok || data.success === false) throw new Error(data.detail || 'This document is not free right now.');
        planPurchaseIdRef.current = data.purchaseId || null;
        localStorage.removeItem('pendingSubTemplate');
        localStorage.removeItem('pendingSubAmount');
      } catch (err) {
        setError(err.message || 'Free download failed. Please try again.');
        setIsVerifying(false);
        return;
      }
      setPaymentVerified(true);
      setIsVerifying(false);
      await generateDocument(null);
      return;
    }
    if (isSubDownload) {
      const token = localStorage.getItem('userToken');
      if (!token) { setError('Sign in to use your subscription.'); setIsVerifying(false); return; }
      try {
        // one download off the plan, before anything generates
        const template = localStorage.getItem('pendingSubTemplate');
        // the wallet path charges the order's exact price; plan and gifted
        // downloads ignore the amount
        const subAmount = parseFloat(localStorage.getItem('pendingSubAmount') || '0') || 0;
        const res = await nativePost(`${BACKEND_URL}/api/user/subscription-download`,
          { documentType: orderType, ...(template ? { template } : {}), count: fileCount, ...(subAmount > 0 ? { amount: subAmount } : {}) },
          { Authorization: `Bearer ${token}` });
        const data = (typeof res.json === 'function' ? await res.json().catch(() => ({})) : res.data) || {};
        if (!res.ok || data.success === false) throw new Error(data.detail || 'Your plan could not cover this download.');
        planPurchaseIdRef.current = data.purchaseId || null;
        localStorage.removeItem('pendingSubTemplate');
        localStorage.removeItem('pendingSubAmount');
        if (data.downloadsRemaining !== undefined || data.wallet) {
          try {
            const u = JSON.parse(localStorage.getItem('userInfo') || 'null');
            if (u && data.wallet) { u.walletCents = Math.round((data.walletBalance || 0) * 100); localStorage.setItem('userInfo', JSON.stringify(u)); }
            else if (u && data.bonus) { u.bonusDownloads = data.downloadsRemaining; localStorage.setItem('userInfo', JSON.stringify(u)); }
            else if (u && u.subscription) { u.subscription.downloads_remaining = data.downloadsRemaining; localStorage.setItem('userInfo', JSON.stringify(u)); }
          } catch {}
        }
      } catch (err) {
        setError(err.message || 'Subscription download failed. Please try again.');
        setIsVerifying(false);
        return;
      }
      setPaymentVerified(true);
      setIsVerifying(false);
      await generateDocument(null);
      return;
    }
    if (!sessionId) {
      if (paymentIntentId) {
        // Embedded card checkout: Stripe.js confirmed the payment on-session
        // before navigating here, so skip session verification entirely.
        // Fire the server-side purchase-record fallback in parallel — the
        // webhook normally records it, but if that's ever missed this keeps
        // Purchases/Calendar in sync with Saved Documents (idempotent).
        nativePost(`${BACKEND_URL}/api/stripe/confirm-payment-intent`, { paymentIntentId }).catch(() => {});
        // A split order: part of it rides the plan's remaining downloads. The
        // card payment is already settled, so a shortfall here (plan changed
        // on another device) never blocks the download the user paid for.
        const subUse = parseInt(searchParams.get('subUse') || '0', 10);
        if (subUse > 0) {
          try {
            const token = localStorage.getItem('userToken');
            if (token) {
              const template = localStorage.getItem('pendingSubTemplate');
              const res = await nativePost(`${BACKEND_URL}/api/user/subscription-download`,
                { documentType: orderType, ...(template ? { template } : {}), count: subUse },
                { Authorization: `Bearer ${token}` });
              const data = (typeof res.json === 'function' ? await res.json().catch(() => ({})) : res.data) || {};
              if (res.ok && data.downloadsRemaining !== undefined) {
                const u = JSON.parse(localStorage.getItem('userInfo') || 'null');
                if (u && data.bonus) { u.bonusDownloads = data.downloadsRemaining; localStorage.setItem('userInfo', JSON.stringify(u)); }
                else if (u && u.subscription) { u.subscription.downloads_remaining = data.downloadsRemaining; localStorage.setItem('userInfo', JSON.stringify(u)); }
              }
            }
          } catch (err) { console.warn('Plan-covered downloads were not consumed:', err); }
        }
        const email = localStorage.getItem('pendingCustomerEmail') || '';
        if (email) setCustomerEmail(email);
        setPaymentVerified(true);
        setIsVerifying(false);
        await generateDocument(email);
        return;
      }
      // No session ID - show error (don't use old cached data)
      setError('No payment session found. Please contact support if you were charged.');
      setIsVerifying(false);
      return;
    }

    try {
      // Step 1: Verify payment with backend
      const response = await fetch(`${BACKEND_URL}/api/stripe/checkout-status/${sessionId}`);
      
      if (!response.ok) {
        // API error - could be invalid session ID
        console.error('Checkout status API error:', response.status);
        // Still try to generate if we have form data - user might have paid
        setPaymentVerified(true);
        setIsVerifying(false);
        await generateDocument();
        return;
      }
      
      const data = await response.json();
      
      // Extract customer email - prefer customer_email from Stripe, fallback to metadata
      const email = data.customer_email || data.metadata?.userEmail || '';
      if (email) {
        setCustomerEmail(email);
      }

      if (data.payment_status === 'paid' || data.status === 'complete') {
        setPaymentVerified(true);
        setIsVerifying(false);
        
        // Step 2: Generate document if we have pending data
        await generateDocument(email);
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setIsVerifying(false);
      } else if (data.status === 'open') {
        // Payment still processing, poll again (max 10 times)
        const pollCount = parseInt(sessionStorage.getItem('paymentPollCount') || '0');
        if (pollCount < 10) {
          sessionStorage.setItem('paymentPollCount', (pollCount + 1).toString());
          setTimeout(verifyAndGenerate, 2000);
        } else {
          // Stop polling, assume payment succeeded and try to generate
          sessionStorage.removeItem('paymentPollCount');
          setPaymentVerified(true);
          setIsVerifying(false);
          await generateDocument();
        }
      } else {
        // Unknown status - assume payment and try to generate
        setPaymentVerified(true);
        setIsVerifying(false);
        await generateDocument();
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      // Network error - still try to generate if we have form data
      setPaymentVerified(true);
      setIsVerifying(false);
      await generateDocument();
    }
  }, [sessionId, paymentIntentId, isSubDownload, isFreeDownload]); // eslint-disable-line

  // Helper function to send file email after generation (PDF or ZIP)
  const sendFileEmail = async (fileBlob, email, documentType, userName = '', isZip = false) => {
    if (!email || !fileBlob) {
      console.log('Skipping email - no email or blob provided');
      return;
    }
    
    try {
      const result = await sendDownloadEmailWithPdf({
        email,
        userName,
        documentType,
        pdfBlob: fileBlob,
        isGuest: !localStorage.getItem('userToken'),
        isZip
      });
      
      if (result.success) {
        setEmailSent(true);
        console.log('Email with attachment sent successfully');
      } else {
        console.error('Failed to send email with attachment:', result.error);
      }
    } catch (err) {
      console.error('Error sending email with attachment:', err);
    }
  };

  // Archive the generated document so it appears in the admin Saved Docs and
  // the purchase's detail modal. Uses the logged-in user endpoint when a token
  // is present, otherwise the guest endpoint keyed by the payer's email.
  const archiveDocument = async (blob, docType, email) => {
    if (!blob) return;
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const isZip = blob.type?.includes("zip");
      const fileName = `${docType}_${new Date().toISOString().split("T")[0]}${isZip ? ".zip" : ".pdf"}`;
      const template = localStorage.getItem(`pending${docType.replace(/(^|-)(\w)/g, (_, __, c) => c.toUpperCase())}Template`) || null;
      // nativePost throughout: plain fetch is CORS-blocked from the native
      // app's WebView origin (capacitor://localhost), which silently dropped
      // every archive made from the native app.
      const token = localStorage.getItem("userToken");
      // Archive by payer email and/or this browser's visitor id — a purchase
      // with no email captured still lands in "My Documents" on this device.
      const canGuest = (email && email.includes("@")) || !!getVisitorId();
      // The Stripe reference ties the archive to its purchase, so the admin
      // purchase modal lists exactly this purchase's file(s); a plan download
      // has none, so its $0 purchase's own id stands in.
      const purchaseRef = paymentIntentId || sessionId || planPurchaseIdRef.current || "";
      const postGuest = () => nativePost(`${BACKEND_URL}/api/guest/saved-documents`, {
        guestEmail: email && email.includes("@") ? email : "", documentType: docType, fileName, fileData: base64, template, purchaseRef,
        // Keys the document to this browser so "My Documents" lists it with no email step
        visitorId: getVisitorId(),
      });
      let res = null;
      if (token) {
        // A stale or expired userToken used to 401 here and the paid document
        // silently never reached Saved Docs. Fall back to the guest archive
        // keyed by the payer email whenever the user endpoint fails.
        try {
          res = await nativePost(`${BACKEND_URL}/api/user/saved-documents`, {
            documentType: docType, fileName, fileData: base64, template, purchaseRef,
          }, { Authorization: `Bearer ${token}` });
        } catch (err) {
          console.warn("User archive request failed:", err);
          res = null;
        }
        if ((!res || !res.ok) && canGuest) {
          console.warn(`User archive failed (${res ? `HTTP ${res.status}` : "network"}) — retrying as guest`);
          res = await postGuest();
        }
      } else if (canGuest) {
        res = await postGuest();
      }
      // Surface a rejected archive (e.g. oversized body) rather than dropping it
      // silently — this is the only reason a paid document would be missing from
      // the admin Saved Docs.
      if (res && !res.ok) {
        console.error(`Failed to archive ${docType}: HTTP ${res.status}`);
      }
      // Remember the archive's id on this browser so "My Documents" can list
      // it even if the server copy carries no visitor id.
      if (res && res.ok) {
        try {
          const data = typeof res.json === "function" ? await res.json() : (res.data || null);
          const id = data && data.documentId;
          if (id) {
            const ids = JSON.parse(localStorage.getItem("ms:docIds") || "[]");
            const next = [...(Array.isArray(ids) ? ids : []).filter((x) => x !== id), id].slice(-100);
            localStorage.setItem("ms:docIds", JSON.stringify(next));
            return id;
          }
        } catch {}
      }
    } catch (err) {
      console.error("Failed to archive document:", err);
    }
    return null;
  };

  // After a successful pay-and-download, reset the originating /app modal's
  // saved form so it doesn't reopen pre-filled. Each modal records exactly
  // which localStorage keys to clear (form data, template, logo…) in
  // `pendingResetKeys` before handing off to checkout. Only called on success —
  // an abandoned or failed payment keeps its data. The flag itself is always
  // dropped in the generateDocument `finally`, so a failed attempt can't leak.
  const clearPaidFormData = () => {
    try {
      const raw = localStorage.getItem('pendingResetKeys');
      if (!raw) return;
      const keys = JSON.parse(raw);
      if (Array.isArray(keys)) keys.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
    } catch {}
  };

  // Generate document based on type
  const generateDocument = async (email = null) => {
    setIsGenerating(true);
    const emailToUse = email || customerEmail;
    let notifId = null;
    let appReturnPath = null;

    try {
      // In-app checkout: the leaf animation needs a few seconds, but never
      // idle-wait BEFORE generating — a tab closed during that pure delay
      // used to lose the paid document entirely (no download, no email, no
      // saved doc; only the webhook-recorded purchase survived). Generate,
      // email, and archive immediately; the hold is awaited afterwards so
      // the animation still plays out fully before returning to the app.
      const animationHold = ((paymentIntentId || isSubDownload || isFreeDownload) && isFromApp)
        ? new Promise((r) => setTimeout(r, 5500))
        : null;

      let generated = false;
      let pdfBlob = null;

      const customDocStr = localStorage.getItem('pendingCustomDocData');
      if (customDocStr) {
        // A user's custom template from any category — its own, an admin's, or a
        // built-in type it was filed under. Render its published layout the same
        // way regardless of category (the owner's from the authed endpoint).
        const { templateId, templateName, values, own } = JSON.parse(customDocStr);
        const layout = await fetchPublishedLayout(templateId, undefined, { user: !!own || orderType.startsWith('ucat-') });
        if (layout) {
          const doc = new (await loadJsPDF())({ unit: 'pt', format: 'letter' });
          renderLayout(doc, layout, values || {}, orderType, { raw: true });
          const safeName = String(templateName || 'document').replace(/[^\w-]+/g, '_');
          const outName = `${safeName}_${new Date().toISOString().split('T')[0]}.pdf`;
          pdfBlob = doc.output('blob');
          generated = true;
          try { sessionStorage.setItem('lastDownloadFileName', outName); } catch {}
          localStorage.removeItem('pendingCustomDocData');
          if (emailToUse && pdfBlob) sendFileEmail(pdfBlob, emailToUse, orderType, templateName);
          toast.success(toastText("document_downloaded", { document: "document" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'paystub') {
        const formDataStr = localStorage.getItem('pendingPaystubData');
        const template = localStorage.getItem('pendingPaystubTemplate') || 'template-a';
        const numStubs = parseInt(localStorage.getItem('pendingPaystubCount') || '1', 10);

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          notifId = `notif_${Date.now()}`;
          // Mirror the generator's real download names so the notification
          // matches the file that actually lands in Downloads.
          const firstPayDate = (formData.payDateList || '').split(',')[0]?.trim() || formData.payDate || new Date();
          const notifFileName = numStubs > 1
            ? getMultiplePaystubsZipFilename(template, formData.name)
            : getIndividualPaystubFilename(template, formData.name, firstPayDate);
          addGeneratingNotification({ id: notifId, type: 'paystub', fileName: notifFileName, fileType: numStubs > 1 ? 'zip' : 'pdf' });

          pdfBlob = await generateAndDownloadPaystub(formData, template, numStubs, true);
          generated = true;

          markNotificationReady(notifId);
          if (isFromApp) appReturnPath = '/app/paystub';

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'paystub', formData.name, numStubs > 1);
          }
        }
      } else if (orderType === 'w2') {
        const formDataStr = localStorage.getItem('pendingW2Data');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pendingW2TaxYear') || '2024';
          
          pdfBlob = await generateAndDownloadW2(formData, taxYear, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'w2', formData.employeeName);
          }
          
          toast.success(toastText("document_downloaded", { document: "W-2" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'w9') {
        const formDataStr = localStorage.getItem('pendingW9Data');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          
          const taxYear = localStorage.getItem('pendingW9TaxYear') || '2024';
          pdfBlob = await generateAndDownloadW9(formData, taxYear, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'w9', formData.name);
          }
          
          toast.success(toastText("document_downloaded", { document: "W-9" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'bank-statement') {
        const formDataStr = localStorage.getItem('pendingBankStatementData');

        if (formDataStr) {
          // Pending data is stored as { formData: {...}, ... } — the generator
          // wants the inner flat object (older saves may already be flat).
          const parsed = JSON.parse(formDataStr);
          const formData = parsed.formData || parsed;
          const template = localStorage.getItem('pendingBankStatementTemplate') || 'template-a';

          pdfBlob = await generateAndDownloadBankStatement(formData, template, true);
          generated = true;

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'bank-statement', formData.accountName);
          }

          toast.success(toastText("document_downloaded", { document: "accounting mockup" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'ai-resume') {
        const resumeDataStr = localStorage.getItem('pendingResumeData');
        
        if (resumeDataStr) {
          const { generatedResume, formData, selectedTemplate } = JSON.parse(resumeDataStr);
          
          if (generatedResume) {
            console.log('Generating AI resume with stored data...', { selectedTemplate });
            
            // Prepare resume data for the generator (carry the style options
            // the builder chose)
            const resumeData = {
              ...generatedResume,
              template: selectedTemplate || formData?.template || 'ats',
              font: formData?.font,
              sectionLayout: formData?.sectionLayout,
              onePage: formData?.onePage,
            };
            
            pdfBlob = await generateAndDownloadResume(resumeData, true);
            generated = true;
            if (isFromApp) appReturnPath = '/app/paystub';

            // Resume is always a ZIP (contains PDF + DOCX)
            if (emailToUse && pdfBlob) {
              sendFileEmail(pdfBlob, emailToUse, 'ai-resume', generatedResume?.personalInfo?.name, true);
            }

            toast.success(toastText("document_downloaded", { document: "AI resume" }, "Your {document} has been downloaded!"));
          }
        }
      } else if (orderType === '1099-nec') {
        const formDataStr = localStorage.getItem('pending1099NECData');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pending1099NECTaxYear') || '2024';
          
          pdfBlob = await generateAndDownload1099NEC(formData, taxYear, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, '1099-nec', formData.recipientName);
          }
          
          toast.success(toastText("document_downloaded", { document: "1099-NEC" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === '1099-misc') {
        const formDataStr = localStorage.getItem('pending1099MISCData');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pending1099MISCTaxYear') || '2024';
          
          pdfBlob = await generateAndDownload1099MISC(formData, taxYear, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, '1099-misc', formData.recipientName);
          }
          
          toast.success(toastText("document_downloaded", { document: "1099-MISC" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'canadian-paystub') {
        const formDataStr = localStorage.getItem('pendingCanadianPaystubData');
        const template = localStorage.getItem('pendingCanadianPaystubTemplate') || 'template-a';
        const numStubs = parseInt(localStorage.getItem('pendingCanadianPaystubCount') || '1', 10);

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          notifId = `notif_${Date.now()}`;
          // Mirror the generator's real download names so the notification
          // matches the file that actually lands in Downloads.
          const firstPayDate = (formData.payDateList || '').split(',')[0]?.trim() || formData.payDate || new Date();
          const notifFileName = numStubs > 1
            ? getCanadianPaystubsZipFilename(template, formData.name)
            : getCanadianPaystubFilename(template, formData.name, firstPayDate);
          addGeneratingNotification({ id: notifId, type: 'canadian-paystub', fileName: notifFileName, fileType: numStubs > 1 ? 'zip' : 'pdf' });

          pdfBlob = await generateAndDownloadCanadianPaystub(formData, template, numStubs, true);
          generated = true;

          markNotificationReady(notifId);
          if (isFromApp) appReturnPath = '/app/canadian-paystub';

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'canadian-paystub', formData.name, numStubs > 1);
          }
        }
      } else if (orderType === 'uk-payslip') {
        const formDataStr = localStorage.getItem('pendingUkPayslipData');
        const template = localStorage.getItem('pendingUkPayslipTemplate') || 'template-h';
        const numStubs = parseInt(localStorage.getItem('pendingUkPayslipCount') || '1', 10);

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          notifId = `notif_${Date.now()}`;
          // Mirror the generator's real download names so the notification
          // matches the file that actually lands in Downloads.
          const firstPayDate = (formData.payDateList || '').split(',')[0]?.trim() || formData.payDate || new Date();
          const notifFileName = numStubs > 1
            ? getUkPayslipsZipFilename(template, formData.name)
            : getUkPayslipFilename(template, formData.name, firstPayDate);
          addGeneratingNotification({ id: notifId, type: 'uk-payslip', fileName: notifFileName, fileType: numStubs > 1 ? 'zip' : 'pdf' });

          pdfBlob = await generateAndDownloadUkPayslip(formData, template, numStubs, true);
          generated = true;

          markNotificationReady(notifId);
          if (isFromApp) appReturnPath = '/app/uk-payslips';

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'uk-payslip', formData.name, numStubs > 1);
          }
        }
      } else if (orderType === 'offer-letter') {
        const formDataStr = localStorage.getItem('pendingOfferLetterData');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          
          pdfBlob = await generateAndDownloadOfferLetter(formData, true);
          generated = true;
          if (isFromApp) appReturnPath = '/app/paystub';

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'offer-letter', formData.recipientName);
          }

          toast.success(toastText("document_downloaded", { document: "offer letter" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'cease-and-desist') {
        const formDataStr = localStorage.getItem('pendingCeaseAndDesistData');

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);

          pdfBlob = await generateAndDownloadCeaseAndDesist(formData, true);
          generated = true;

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'cease-and-desist', formData.senderName);
          }

          toast.success(toastText("document_downloaded", { document: "cease and desist letter" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'legal-document') {
        const formDataStr = localStorage.getItem('pendingLegalDocumentData');

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);

          pdfBlob = await generateAndDownloadLegalDocument(formData, true);
          generated = true;

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'legal-document', formData.partyAName);
          }

          toast.success(toastText("document_downloaded", { document: "legal document" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'power-of-attorney') {
        const formDataStr = localStorage.getItem('pendingPowerOfAttorneyData');

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);

          pdfBlob = await generateAndDownloadPowerOfAttorney(formData, true);
          generated = true;

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'power-of-attorney', formData.principalName);
          }

          toast.success(toastText("document_downloaded", { document: "power of attorney" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'commercial-lease') {
        const formDataStr = localStorage.getItem('pendingCommercialLeaseData');

        if (formDataStr) {
          const formData = JSON.parse(formDataStr);

          pdfBlob = await generateAndDownloadCommercialLease(formData, true);
          generated = true;

          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'commercial-lease', formData.landlordName);
          }

          toast.success(toastText("document_downloaded", { document: "commercial lease" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'schedule-c') {
        const formDataStr = localStorage.getItem('pendingScheduleCData');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pendingScheduleCTaxYear') || '2024';
          
          pdfBlob = await generateAndDownloadScheduleC(formData, taxYear, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'schedule-c', formData.businessName);
          }
          
          toast.success(toastText("document_downloaded", { document: "Schedule C" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'utility-bill') {
        const formDataStr = localStorage.getItem('pendingUtilityBillData');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const template = localStorage.getItem('pendingUtilityBillTemplate') || 'electric';
          
          pdfBlob = await generateAndDownloadUtilityBill(formData, template, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'utility-bill', formData.customerName);
          }
          
          toast.success(toastText("document_downloaded", { document: "utility bill" }, "Your {document} has been downloaded!"));
        }
      } else if (orderType === 'vehicle-bill-of-sale') {
        const formDataStr = localStorage.getItem('pendingVehicleBillOfSaleData');
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          
          pdfBlob = await generateAndDownloadVehicleBillOfSale(formData, true);
          generated = true;
          
          if (emailToUse && pdfBlob) {
            sendFileEmail(pdfBlob, emailToUse, 'vehicle-bill-of-sale', formData.buyerName);
          }
          
          toast.success(toastText("document_downloaded", { document: "vehicle bill of sale" }, "Your {document} has been downloaded!"));
        }
      }
      // Add more document types as needed...
      
      if (generated) {
        // Archive the paid document (admin Saved Docs + purchase modal).
        // 'ai-resume' is stored under the 'resume' document type used elsewhere.
        const archivedId = await archiveDocument(pdfBlob, orderType === 'ai-resume' ? 'resume' : orderType, emailToUse);

        // The generators only return the blob when asked for it, so nothing has
        // been downloaded yet; the name is needed here for the drawer entry.
        const downloadName = sessionStorage.getItem('lastDownloadFileName')
          || localStorage.getItem('lastDownloadFileName')
          || getDefaultFileName(orderType, fileCount);

        // Every paid document earns a drawer entry. The pay stub branches make
        // their own above, because they know the real multi-stub file names;
        // every other type gets one here. This used to sit inside the embedded
        // in-app branch below, so a legal, tax or business document bought
        // through any other checkout route reached the drawer as nothing.
        if (!notifId) {
          notifId = `notif_${Date.now()}`;
          addGeneratingNotification({ id: notifId, type: orderType, fileName: downloadName, fileType: downloadName.endsWith('.zip') ? 'zip' : 'pdf' });
          markNotificationReady(notifId);
        }
        // The drawer's notification points at the archived copy, so re-download
        // and preview work from any device once the account has it.
        if (archivedId) setNotificationDocId(notifId, archivedId);

        // Paid and downloaded — clear the originating /app modal's saved form.
        clearPaidFormData();

        // Only now let the success animation finish out — the document is
        // already downloaded, emailed, and archived.
        if (animationHold) await animationHold;

        // Navigate back to app for app-managed document types — but only for
        // the legacy hosted-checkout redirect.
        if (appReturnPath && !paymentIntentId && !isSubDownload && !isFreeDownload) {
          navigate(appReturnPath);
          return;
        }

        // Embedded in-app checkout and subscription downloads: return to the
        // app and let it download the file once it is on screen (phones open
        // it in a new tab so this tab is never replaced by the PDF). The web
        // success card is for hosted-checkout buyers.
        if ((paymentIntentId || isSubDownload || isFreeDownload) && isFromApp) {
          setPendingDownload(pdfBlob, downloadName);
          try { localStorage.setItem('appOpenNotifDrawer', '1'); } catch {}
          if (!isSubDownload && !isFreeDownload) await stashUpsellForApp(pdfBlob);
          navigate(appReturnTo);
          return;
        }

        if (pdfBlob) deliverDownload(pdfBlob, downloadName);
        setDocumentGenerated(true);

        // Check for stored download URL from the generator (check both localStorage and sessionStorage)
        const storedUrl = localStorage.getItem('lastDownloadUrl') || sessionStorage.getItem('lastDownloadUrl');
        const storedName = localStorage.getItem('lastDownloadFileName') || sessionStorage.getItem('lastDownloadFileName');
        if (storedUrl) {
          setDownloadUrl(storedUrl);
          setFileName(storedName || getDefaultFileName(orderType, fileCount));
        } else {
          setFileName(getDefaultFileName(orderType, fileCount));
        }
      } else {
        // No stored data found - show manual download option
        console.error(`Generation skipped: no pending form data found for orderType="${orderType}" (expected a matching "pending…Data" key in localStorage)`);
        if (returnAppOnFailure(notifId)) return;
        setError('Unable to generate document automatically. Your form data may have been lost. Please try creating your document again.');
      }
    } catch (err) {
      // Surface the real reason — this is what a "generation failed" drawer
      // notification actually means, and it was previously invisible.
      console.error(`Error generating document (orderType="${orderType}"):`, err && err.stack ? err.stack : err);
      if (notifId) markNotificationError(notifId);
      if (returnAppOnFailure(notifId)) return;
      setError('There was an issue generating your document. Please contact support.');
    } finally {
      setIsGenerating(false);
      // Always drop the reset flag — cleared forms already consumed it, and a
      // failed attempt must not leave it to clear a later, unrelated form.
      try { localStorage.removeItem('pendingResetKeys'); } catch {}
    }
  };

  // Embedded in-app purchases must never strand the buyer on the web card —
  // on a generation failure, drop a failed notification in the drawer and
  // return to the app (support can resend the file; the purchase is recorded).
  const returnAppOnFailure = (existingNotifId) => {
    if (!(paymentIntentId && isFromApp)) return false;
    if (!existingNotifId) {
      const nid = `notif_${Date.now()}`;
      addGeneratingNotification({ id: nid, type: orderType, fileName: getDefaultFileName(orderType, fileCount), fileType: 'pdf' });
      markNotificationError(nid);
    }
    try { localStorage.setItem('appOpenNotifDrawer', '1'); } catch {}
    navigate(appReturnTo);
    return true;
  };

  const getDefaultFileName = (type, count) => {
    const names = {
      'paystub': count > 1 ? `paystubs_${count}.zip` : 'paystub.pdf',
      'canadian-paystub': count > 1 ? `canadian_paystubs_${count}.zip` : 'canadian_paystub.pdf',
      'uk-payslip': count > 1 ? `uk_payslips_${count}.zip` : 'uk_payslip.pdf',
      'w2': 'w2_form.pdf',
      'w9': 'w9_form.pdf',
      '1099-misc': '1099_misc.pdf',
      '1099-nec': '1099_nec.pdf',
      'bank-statement': 'accounting_mockup.pdf',
      'offer-letter': 'offer_letter.pdf',
      'cease-and-desist': 'cease_and_desist_letter.pdf',
      'legal-document': 'legal_document.pdf',
      'power-of-attorney': 'durable_power_of_attorney.pdf',
      'commercial-lease': 'commercial_lease_agreement.pdf',
      'schedule-c': 'schedule_c.pdf',
      'utility-bill': 'utility_bill.pdf',
      'vehicle-bill-of-sale': 'vehicle_bill_of_sale.pdf',
      'ai-resume': 'resume.zip'
    };
    return names[type] || 'document.pdf';
  };

  const getDocumentTypeDisplay = (type) => {
    const names = {
      'paystub': 'Pay Stub',
      'canadian-paystub': 'Canadian Pay Stub',
      'uk-payslip': 'UK Payslip',
      'w2': 'W-2 Form',
      'w9': 'W-9 Form',
      '1099-misc': '1099-MISC Form',
      '1099-nec': '1099-NEC Form',
      'bank-statement': 'Accounting Mockup',
      'offer-letter': 'Offer Letter',
      'cease-and-desist': 'Cease and Desist Letter',
      'legal-document': 'Legal Document',
      'power-of-attorney': 'Power of Attorney',
      'commercial-lease': 'Commercial Lease Agreement',
      'schedule-c': 'Schedule C',
      'utility-bill': 'Utility Bill',
      'vehicle-bill-of-sale': 'Vehicle Bill of Sale',
      'ai-resume': 'AI Resume'
    };
    return names[type] || 'Document';
  };

  // Handle manual re-download - regenerate the document
  const handleRedownload = async () => {
    setIsGenerating(true);
    toast.info('Preparing your download...');
    
    try {
      // Try to regenerate using stored form data
      let regenerated = false;
      
      if (orderType === 'paystub') {
        const formDataStr = localStorage.getItem('pendingPaystubData');
        const template = localStorage.getItem('pendingPaystubTemplate') || 'template-a';
        const numStubs = parseInt(localStorage.getItem('pendingPaystubCount') || fileCount.toString(), 10);
        
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadPaystub(formData, template, numStubs);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'w2') {
        const formDataStr = localStorage.getItem('pendingW2Data');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pendingW2TaxYear') || '2024';
          await generateAndDownloadW2(formData, taxYear);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'w9') {
        const formDataStr = localStorage.getItem('pendingW9Data');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadW9(formData, localStorage.getItem('pendingW9TaxYear') || '2024');
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'bank-statement') {
        const formDataStr = localStorage.getItem('pendingBankStatementData');
        if (formDataStr) {
          const parsed = JSON.parse(formDataStr);
          const formData = parsed.formData || parsed;
          const template = localStorage.getItem('pendingBankStatementTemplate') || 'template-a';
          await generateAndDownloadBankStatement(formData, template);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'ai-resume') {
        const resumeDataStr = localStorage.getItem('pendingResumeData');
        if (resumeDataStr) {
          const { generatedResume, formData, selectedTemplate } = JSON.parse(resumeDataStr);
          if (generatedResume) {
            const resumeData = {
              ...generatedResume,
              template: selectedTemplate || formData?.template || 'ats'
            };
            await generateAndDownloadResume(resumeData);
            regenerated = true;
            toast.success(toastText("download_started", {}, "Download started!"));
          }
        }
      } else if (orderType === '1099-nec') {
        const formDataStr = localStorage.getItem('pending1099NECData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pending1099NECTaxYear') || '2024';
          await generateAndDownload1099NEC(formData, taxYear);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === '1099-misc') {
        const formDataStr = localStorage.getItem('pending1099MISCData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pending1099MISCTaxYear') || '2024';
          await generateAndDownload1099MISC(formData, taxYear);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'canadian-paystub') {
        const formDataStr = localStorage.getItem('pendingCanadianPaystubData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const template = localStorage.getItem('pendingCanadianPaystubTemplate') || 'template-a';
          const numStubs = parseInt(localStorage.getItem('pendingCanadianPaystubCount') || fileCount.toString(), 10);
          await generateAndDownloadCanadianPaystub(formData, template, numStubs);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'uk-payslip') {
        const formDataStr = localStorage.getItem('pendingUkPayslipData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const template = localStorage.getItem('pendingUkPayslipTemplate') || 'template-h';
          const numStubs = parseInt(localStorage.getItem('pendingUkPayslipCount') || fileCount.toString(), 10);
          await generateAndDownloadUkPayslip(formData, template, numStubs);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'offer-letter') {
        const formDataStr = localStorage.getItem('pendingOfferLetterData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadOfferLetter(formData);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'cease-and-desist') {
        const formDataStr = localStorage.getItem('pendingCeaseAndDesistData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadCeaseAndDesist(formData);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'legal-document') {
        const formDataStr = localStorage.getItem('pendingLegalDocumentData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadLegalDocument(formData);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'power-of-attorney') {
        const formDataStr = localStorage.getItem('pendingPowerOfAttorneyData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadPowerOfAttorney(formData);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'commercial-lease') {
        const formDataStr = localStorage.getItem('pendingCommercialLeaseData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadCommercialLease(formData);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'schedule-c') {
        const formDataStr = localStorage.getItem('pendingScheduleCData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const taxYear = localStorage.getItem('pendingScheduleCTaxYear') || '2024';
          await generateAndDownloadScheduleC(formData, taxYear);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'utility-bill') {
        const formDataStr = localStorage.getItem('pendingUtilityBillData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          const template = localStorage.getItem('pendingUtilityBillTemplate') || 'electric';
          await generateAndDownloadUtilityBill(formData, template);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      } else if (orderType === 'vehicle-bill-of-sale') {
        const formDataStr = localStorage.getItem('pendingVehicleBillOfSaleData');
        if (formDataStr) {
          const formData = JSON.parse(formDataStr);
          await generateAndDownloadVehicleBillOfSale(formData);
          regenerated = true;
          toast.success(toastText("download_started", {}, "Download started!"));
        }
      }
      
      if (!regenerated) {
        // Try using blob URL as last resort
        if (downloadUrl) {
          openDownloadInNewTab({ url: downloadUrl, fileName: fileName || 'document.pdf' });
          toast.success(toastText("download_started", {}, "Download started!"));
        } else {
          toast.error('Unable to download. Please try creating your document again.');
        }
      }
    } catch (err) {
      console.error('Error regenerating document:', err);
      toast.error('Download failed. Please try creating your document again.');
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    // Prevent double execution (React StrictMode runs effects twice in dev)
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    verifyAndGenerate();
  }, [verifyAndGenerate]);

  // Track conversion for Google Analytics
  useEffect(() => {
    if (paymentVerified && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'purchase', {
        transaction_id: sessionId || paymentIntentId || `order_${Date.now()}`,
        value: orderType === 'paystub' ? 9.99 : 14.99,
        currency: 'USD',
        items: [{
          item_id: orderType,
          item_name: getDocumentTypeDisplay(orderType),
          quantity: 1
        }]
      });
    }
  }, [paymentVerified, sessionId, paymentIntentId, orderType]);

  // Loading state while verifying payment
  if (isVerifying) {
    return <ProcessingScreen title="Verifying Payment" messages={VERIFY_MESSAGES} />;
  }

  // Generating state
  if (isGenerating) {
    return <ProcessingScreen title={`Generating Your ${getDocumentTypeDisplay(orderType)}`} messages={GENERATE_MESSAGES} />;
  }

  // Error state
  if (error && !paymentVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-amber-100 p-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800 text-center mb-2">Something Went Wrong</h2>
            <p className="text-slate-600 text-center mb-6">{error}</p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate(isFromApp ? appReturnTo : `/${orderType}-generator`)}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                Try Again
              </Button>
              <a 
                href="mailto:support@mintslip.com?subject=Payment%20Issue" 
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium transition-colors"
              >
                <Mail className="w-5 h-5" />
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      {/* CSS Animation Styles */}
      <style>{`
        @keyframes checkmark-pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes checkmark-draw {
          0% { stroke-dashoffset: 50; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes pulse-ring {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(1.4); opacity: 0; }
        }
        .checkmark-container {
          animation: checkmark-pop 0.5s ease-out forwards;
        }
        .checkmark-icon {
          animation: checkmark-draw 0.6s ease-out 0.3s forwards;
        }
        .pulse-ring {
          animation: pulse-ring 1.5s ease-out infinite;
        }
      `}</style>
      
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
          {/* Header with animated checkmark */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-600 px-8 py-10 text-center">
            <div className="relative inline-flex items-center justify-center">
              <div className="absolute w-24 h-24 bg-white/20 rounded-full pulse-ring"></div>
              <div className="checkmark-container relative inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg">
                <svg 
                  className="w-12 h-12" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path 
                    className="checkmark-icon text-emerald-600" 
                    d="M5 13l4 4L19 7"
                    style={{ 
                      stroke: '#34d399',
                      strokeDasharray: 50,
                      strokeDashoffset: 50 
                    }}
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2 mt-4">{(isSubDownload || isFreeDownload) ? "Download Ready!" : "Payment Successful!"}</h1>
            <p className="text-emerald-100">{isSubDownload ? "Covered by your subscription" : "Thank you for your purchase"}</p>
          </div>
          
          {/* Content */}
          <div className="px-8 py-8 space-y-6">
            {/* Order confirmation */}
            {(sessionId || paymentIntentId) && (
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <p className="text-sm text-emerald-700">Order Reference</p>
                <p className="font-mono text-sm font-semibold text-emerald-800 truncate">{sessionId || paymentIntentId}</p>
              </div>
            )}
            
            {/* Your Download Section */}
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <Download className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-slate-800 text-lg">Your Download</h3>
              </div>
              
              {documentGenerated ? (
                <>
                  <p className="text-sm text-slate-600 mb-4">
                    Your {getDocumentTypeDisplay(orderType)} has been downloaded. If it didn't download, click below to download again.
                  </p>
                  
                  <button
                    onClick={handleRedownload}
                    className="w-full flex items-center gap-4 p-4 bg-white border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-xl transition-all group cursor-pointer"
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isZipFile ? 'bg-amber-100' : 'bg-red-100'}`}>
                      {isZipFile ? (
                        <FolderArchive className="w-7 h-7 text-amber-600" />
                      ) : (
                        <FileText className="w-7 h-7 text-red-600" />
                      )}
                    </div>
                    
                    <div className="flex-1 text-left">
                      <p className="font-medium text-slate-800 group-hover:text-emerald-700 transition-colors">
                        {fileName || getDefaultFileName(orderType, fileCount)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {isZipFile ? 'ZIP Archive' : 'PDF Document'}
                      </p>
                    </div>
                    
                    <div className="w-10 h-10 rounded-full bg-emerald-100 group-hover:bg-emerald-600 flex items-center justify-center transition-colors">
                      <Download className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                    </div>
                  </button>
                </>
              ) : (
                <div className="text-center py-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-600 mb-4">
                    {error || 'We couldn\'t automatically generate your document. Please try creating it again.'}
                  </p>
                  <Button
                    onClick={() => navigate(isFromApp ? appReturnTo : `/${orderType}-generator`)}
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  >
                    Create Document Again
                  </Button>
                </div>
              )}
            </div>
            
            {/* Post-purchase upsell — matching documents at a discount while the countdown runs */}
            {upsell && upsellLeft > 0 && (
              <div className="rounded-xl p-5 border border-emerald-200" style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)" }}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Limited-time offer</p>
                    <h3 className="font-semibold text-slate-800 text-lg mt-0.5">
                      Need the matching {upsell.targets.length === 1 ? upsell.targets[0].label : "document"}? {upsell.discountPercent}% off
                    </h3>
                  </div>
                  <span className="font-mono text-sm font-bold text-emerald-700 bg-white border border-emerald-200 rounded-lg px-2.5 py-1 whitespace-nowrap">
                    {String(Math.floor(upsellLeft / 60)).padStart(2, "0")}:{String(upsellLeft % 60).padStart(2, "0")}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Customers who buy a {getDocumentTypeDisplay(orderType)} usually need these too. The discount applies automatically at checkout while the timer is running.
                </p>
                <div className="flex flex-col gap-2">
                  {upsell.targets.map((t) => (
                    <button
                      key={t.documentType}
                      onClick={() => takeUpsell(t)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border-2 border-emerald-200 hover:border-emerald-500 rounded-xl transition-all text-left"
                    >
                      <span className="font-medium text-slate-800">Create a {t.label}</span>
                      <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">{upsell.discountPercent}% off →</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Support info */}
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
              <HelpCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-800">Need Help?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  If you have any issues or questions about your document, please don't hesitate to contact us.
                </p>
              </div>
            </div>
            
            {/* Contact button */}
            <a 
              href={`mailto:support@mintslip.com?subject=Support%20Request%20-%20${sessionId || 'Order'}`} 
              className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium transition-colors"
            >
              <Mail className="w-5 h-5" />
              Contact Support
            </a>
            
            {/* Divider */}
            <div className="border-t border-slate-200"></div>
            
            {/* Back button */}
            <Button
              onClick={() => navigate(isFromApp ? appReturnTo : '/')}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold rounded-xl"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {isFromApp ? 'Back to App' : 'Back to Home'}
            </Button>

            {/* Create another */}
            <button
              onClick={() => navigate(isFromApp ? appReturnTo : `/${orderType === 'canadian-paystub' ? 'canadian-paystub' : orderType === 'uk-payslip' ? 'uk-payslip' : orderType}-generator`)}
              className="w-full text-center text-emerald-600 hover:text-emerald-700 font-medium py-2"
            >
              Create Another Document →
            </button>
          </div>
        </div>
        
        {/* Footer note */}
        <p className="text-center text-sm text-slate-500 mt-6">
          A confirmation email has been sent to your email address.
        </p>
      </div>
    </div>
  );
}
